import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { cloneElement, isValidElement, useEffect, useId, useLayoutEffect, useRef, useState, } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, Circle, CircleDot, Info, OctagonAlert, UserRound, X, } from 'lucide-react';
import { cn } from '../lib/cn.js';
import { Portal, anchoredStyle, useAnchoredPosition } from '../lib/overlay.js';
import { uiStrings } from '../strings.js';
import { Menu } from './overlays.js';
// v3.5: yumuşak rozet kenarı belirginleşti; `solid` dolu zemin + beyaz metin (kontrast tokens.test.ts'te ≥ 4,5:1).
// Tema uyarlaması: hap biçimi (rounded-pill), yumuşak zemin + renkli metin; kenar tonun %30'u (açık zeminlerde hap seçilsin)
const BADGE = {
    neutral: 'bg-surface-subtle text-fg-muted border-border-strong/40',
    primary: 'bg-primary-subtle text-primary border-primary/30',
    info: 'bg-info-subtle text-info border-info/30',
    success: 'bg-success-subtle text-success border-success/30',
    warning: 'bg-warning-subtle text-warning border-warning/30',
    danger: 'bg-danger-subtle text-danger border-danger/30',
};
/** Tonun varsayılan şekli (`toneIcon`): durum renk dışında şekille de ayrışır */
const BADGE_ICON = {
    neutral: Circle,
    primary: CircleDot,
    info: Info,
    success: CheckCircle2,
    warning: AlertTriangle,
    danger: OctagonAlert,
};
const BADGE_SOLID = {
    neutral: 'bg-fg-muted text-primary-foreground border-transparent',
    primary: 'bg-primary text-primary-foreground border-transparent',
    info: 'bg-info text-primary-foreground border-transparent',
    success: 'bg-success text-primary-foreground border-transparent',
    warning: 'bg-warning text-primary-foreground border-transparent',
    danger: 'bg-danger text-primary-foreground border-transparent',
};
/**
 * Durum rozeti (tema uyarlaması): hap, yumuşak zemin + renkli metin; metin HER ZAMAN görünür. Durum ayrıca şekille de
 * anlatılabilir: `icon` (çağıranın simgesi) ya da `toneIcon` (tonun varsayılan şekli — onay, uyarı üçgeni, sekizgen…).
 */
export function Badge({ tone = 'neutral', variant = 'soft', icon, toneIcon = false, children, className, }) {
    const ToneIcon = BADGE_ICON[tone];
    const shape = icon ?? (toneIcon ? _jsx(ToneIcon, { size: 12, strokeWidth: 2.25 }) : null);
    return (_jsxs("span", { className: cn('inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-pill border px-2 py-0.5 text-xs font-medium', variant === 'solid' ? BADGE_SOLID[tone] : BADGE[tone], className), children: [shape ? (_jsx("span", { "aria-hidden": "true", className: "inline-flex shrink-0", children: shape })) : null, _jsx("span", { className: "truncate", children: children })] }));
}
const TAG_CLASS = {
    gray: 'bg-tag-gray-bg text-tag-gray-fg',
    red: 'bg-tag-red-bg text-tag-red-fg',
    orange: 'bg-tag-orange-bg text-tag-orange-fg',
    yellow: 'bg-tag-yellow-bg text-tag-yellow-fg',
    green: 'bg-tag-green-bg text-tag-green-fg',
    teal: 'bg-tag-teal-bg text-tag-teal-fg',
    blue: 'bg-tag-blue-bg text-tag-blue-fg',
    indigo: 'bg-tag-indigo-bg text-tag-indigo-fg',
    purple: 'bg-tag-purple-bg text-tag-purple-fg',
    pink: 'bg-tag-pink-bg text-tag-pink-fg',
};
export function TagChip({ name, color = 'gray', onRemove, removeLabel, maxWidthClass = 'max-w-[14rem]', }) {
    return (_jsxs("span", { className: cn('inline-flex items-center gap-1 rounded-sm py-0.5 pl-1.5 text-xs font-medium', onRemove ? 'pr-0.5' : 'pr-1.5', TAG_CLASS[color], maxWidthClass), title: name, children: [_jsx("span", { className: "truncate", children: name }), onRemove ? (_jsx("button", { type: "button", onClick: onRemove, "aria-label": removeLabel ?? uiStrings.removeTag(name), className: "inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm hover:bg-fg/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus", children: _jsx(X, { size: 12, "aria-hidden": "true" }) })) : null] }));
}
// ---------------------------------------------------------------------------
// Avatar: baş harfler (fotoğraf yok; kişisel veri minimum)
// ---------------------------------------------------------------------------
/**
 * Baş harfler: ilk ve son HARF içeren sözcüğün ilk harfi (Unicode; Türkçe büyük harf kuralıyla). Z6 (PM incelemesi 04.11):
 * harf taşımayan parçalar (kayıt no eki "(#1)", tire, rakam, noktalama) sayılmaz — önceden "Kerem Deneme (#1)" → "K(" çiziliyordu.
 * Hiç harf yoksa boş döner; `Avatar` o zaman nötr kişi simgesi çizer (ad ya da başlık değiştirilmez).
 */
export function initialsOf(name) {
    const letters = name
        .trim()
        .split(/\s+/)
        .map((part) => /\p{L}/u.exec(part)?.[0] ?? '')
        .filter((c) => c !== '');
    const first = letters[0] ?? '';
    const last = letters.length > 1 ? (letters[letters.length - 1] ?? '') : '';
    return (first + last).toLocaleUpperCase('tr-TR');
}
export function Avatar({ name, size = 'md', decorative = false, shape = 'circle', icon, className, }) {
    const initials = initialsOf(name);
    return (_jsx("span", { role: decorative ? undefined : 'img', "aria-label": decorative ? undefined : name, "aria-hidden": decorative || undefined, className: cn('inline-flex shrink-0 select-none items-center justify-center border border-primary/20 bg-primary-subtle font-semibold text-primary', shape === 'square' ? (size === 'xl' ? 'rounded-lg' : 'rounded-md') : 'rounded-full', size === 'sm'
            ? 'h-6 w-6 text-[11px]'
            : size === 'lg'
                ? 'h-11 w-11 text-base'
                : size === 'xl'
                    ? 'h-16 w-16 text-xl'
                    : 'h-8 w-8 text-xs', className), children: icon ??
            (initials === '' ? _jsx(UserRound, { "aria-hidden": "true", className: "h-[60%] w-[60%]" }) : initials) }));
}
// ---------------------------------------------------------------------------
// Tooltip: yalnız tamamlayıcı bilgi (asıl ad/etiket tooltip'e saklanmaz); odak ve üzerine gelmeyle açılır
// ---------------------------------------------------------------------------
export function Tooltip({ content, children, describe = true, }) {
    const [open, setOpen] = useState(false);
    const id = `tt${useId().replace(/:/g, '')}`;
    const anchorRef = useRef(null);
    const layerRef = useRef(null);
    const pos = useAnchoredPosition(open, anchorRef, layerRef, 'start');
    const timer = useRef(undefined);
    const show = () => {
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => {
            setOpen(true);
        }, 300);
    };
    const hide = () => {
        window.clearTimeout(timer.current);
        setOpen(false);
    };
    const child = describe && isValidElement(children)
        ? cloneElement(children, { 'aria-describedby': open ? id : undefined })
        : children;
    return (_jsxs("span", { ref: anchorRef, className: "inline-flex", onMouseEnter: show, onMouseLeave: hide, onFocus: show, onBlur: hide, onKeyDown: (e) => {
            if (e.key === 'Escape')
                hide();
        }, children: [child, open ? (_jsx(Portal, { anchorRef: anchorRef, children: _jsx("div", { ref: layerRef, id: id, role: "tooltip", style: anchoredStyle(pos), className: "pointer-events-none fixed z-tooltip max-w-xs rounded-sm bg-fg px-2 py-1 text-xs text-surface", children: content }) })) : null] }));
}
const TAB_GAP = 4; // gap-1
/**
 * Sekmeler (tema uyarlaması, 20 Ekim 2026):
 *  - `underline` (varsayılan): alt çizgili; etkin sekme vurgu rengi metin + 2 px alt çizgi, 14/600, en az 44 px.
 *  - `pills`: bölüm seçimi için hap (segment) grubu — açık zeminde etkin sekme beyaz yüzey.
 *  - `overflow="menu"`: sığmayan sekmeler uzun yatay şerit yerine sondaki "Diğer ▾" menüsüne iner; seçili sekme her zaman
 *    görünür kalır (gerekirse son görünür yere alınır). Varsayılan `scroll` (eski davranış: şerit kendi içinde kayar) —
 *    menüye taşıma ekranı kullanan ajan/test bunu bilerek açar (menüdeki öğe `role="tab"` değil `menuitem`'dır).
 *    `TabItem.secondary` sekmeler her genişlikte menüdedir (birincil sekmeler kısa kalır; uzun yatay şerit olmaz).
 * Ölçüm yalnız tarayıcıda yapılır (ResizeObserver); ölçüm yoksa (sunucu, jsdom) birincil sekmelerin hepsi çizilir.
 */
export function Tabs({ items, value, onChange, label, className, variant = 'underline', overflow = 'scroll', moreLabel = uiStrings.moreTabs, panelClassName, testId, }) {
    const base = useId().replace(/:/g, '');
    const refs = useRef({});
    const rowRef = useRef(null);
    const moreRef = useRef(null);
    const widths = useRef(new Map());
    const moreWidth = useRef(96);
    const pendingFocus = useRef(null);
    const listRef = useRef(null);
    // Z6 (3 Kasım 2026): `scroll` kipinde şeridin kenarında kalan (kesilen) sekme var mı — görünür kaydırma ipucu için
    const [edges, setEdges] = useState({
        start: false,
        end: false,
    });
    // Sığan sekme sayısı (null: ölçülmedi → hepsi çizilir)
    const [fit, setFit] = useState(null);
    const enabled = items.filter((t) => !t.disabled);
    // Şeride aday sekmeler: `menu` kipinde ikincil sekmeler yalnız seçiliyken (birincillerin ardından) şeride girer
    const strip = overflow === 'menu' && items.some((t) => t.secondary)
        ? [
            ...items.filter((t) => !t.secondary),
            ...items.filter((t) => t.secondary && t.id === value),
        ]
        : items;
    const itemsKey = strip.map((t) => t.id).join('|');
    useLayoutEffect(() => {
        if (overflow !== 'menu')
            return;
        const row = rowRef.current;
        if (!row || typeof ResizeObserver === 'undefined')
            return;
        const compute = () => {
            for (const t of strip) {
                const el = refs.current[t.id];
                if (el)
                    widths.current.set(t.id, el.offsetWidth);
            }
            if (moreRef.current)
                moreWidth.current = moreRef.current.offsetWidth;
            const avail = row.clientWidth;
            const ws = strip.map((t) => widths.current.get(t.id) ?? 0);
            if (avail <= 0 || ws.some((w) => w === 0))
                return;
            // İkincil sekme varsa "Diğer" düğmesi her zaman çizilir: yeri baştan ayrılır
            const reserve = strip.length < items.length ? moreWidth.current + TAB_GAP : 0;
            const total = ws.reduce((a, w) => a + w, 0) + TAB_GAP * (ws.length - 1) + reserve;
            if (total <= avail) {
                setFit(strip.length);
                return;
            }
            let used = moreWidth.current + TAB_GAP;
            let n = 0;
            for (const w of ws) {
                if (used + w > avail)
                    break;
                used += w + TAB_GAP;
                n += 1;
            }
            setFit(Math.max(1, n));
        };
        compute();
        const ro = new ResizeObserver(compute);
        ro.observe(row);
        return () => {
            ro.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- sekme kimlikleri değişince yeniden ölçülür
    }, [overflow, itemsKey]);
    // `scroll` kipi: şerit taştığında hangi kenarda gizli sekme kaldığını izler (ölçüm yalnız tarayıcıda; jsdom'da ipucu yok)
    useLayoutEffect(() => {
        if (overflow !== 'scroll')
            return;
        const el = listRef.current;
        if (!el || typeof ResizeObserver === 'undefined')
            return;
        const update = () => {
            const max = el.scrollWidth - el.clientWidth;
            const start = max > 1 && el.scrollLeft > 1;
            const end = max > 1 && max - el.scrollLeft > 1;
            setEdges((p) => (p.start === start && p.end === end ? p : { start, end }));
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        el.addEventListener('scroll', update, { passive: true });
        return () => {
            ro.disconnect();
            el.removeEventListener('scroll', update);
        };
    }, [overflow, itemsKey]);
    // `scroll` kipi: seçili sekme şeridin görünen bölümüne alınır (yalnız yatay; sayfa dikeyde kaydırılmaz)
    useEffect(() => {
        if (overflow !== 'scroll')
            return;
        const list = listRef.current;
        const tab = refs.current[value];
        if (!list || !tab || list.scrollWidth <= list.clientWidth)
            return;
        const left = tab.offsetLeft;
        const right = left + tab.offsetWidth;
        if (left < list.scrollLeft)
            list.scrollLeft = left;
        else if (right > list.scrollLeft + list.clientWidth)
            list.scrollLeft = right - list.clientWidth;
    }, [overflow, value]);
    function scrollStrip(dir) {
        const list = listRef.current;
        if (!list)
            return;
        const reduce = typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const by = Math.max(80, Math.round(list.clientWidth * 0.7)) * dir;
        if (typeof list.scrollBy === 'function')
            list.scrollBy({ left: by, behavior: reduce ? 'auto' : 'smooth' });
        else
            list.scrollLeft += by;
    }
    // Görünür sekmeler: şeride aday sekmelerin ilk `fit` tanesi; seçili sekme dışarıda kalırsa son görünür yere alınır
    let shown = strip;
    if (overflow === 'menu' && fit !== null && fit < strip.length) {
        const head = strip.slice(0, fit);
        const sel = strip.find((t) => t.id === value);
        shown = sel && !head.includes(sel) ? [...head.slice(0, Math.max(0, fit - 1)), sel] : head;
    }
    const hidden = overflow === 'menu' ? items.filter((t) => !shown.includes(t)) : [];
    useEffect(() => {
        const id = pendingFocus.current;
        if (id === null)
            return;
        pendingFocus.current = null;
        refs.current[id]?.focus();
    });
    function onKey(e) {
        const i = enabled.findIndex((t) => t.id === value);
        let next;
        if (e.key === 'ArrowRight')
            next = enabled[(i + 1) % enabled.length];
        else if (e.key === 'ArrowLeft')
            next = enabled[(i - 1 + enabled.length) % enabled.length];
        else if (e.key === 'Home')
            next = enabled[0];
        else if (e.key === 'End')
            next = enabled[enabled.length - 1];
        if (next) {
            e.preventDefault();
            onChange(next.id);
            // Menüdeki sekme seçilince görünür yere alınır; odak bir sonraki çizimde ona verilir
            const el = refs.current[next.id];
            if (el)
                el.focus();
            else
                pendingFocus.current = next.id;
        }
    }
    const current = items.find((t) => t.id === value);
    const pills = variant === 'pills';
    return (_jsxs("div", { className: className, "data-testid": testId, children: [_jsxs("div", { ref: rowRef, className: cn('flex min-w-0 items-end', !pills && 'border-b border-border', pills && 'items-center'), children: [_jsxs("div", { className: "relative flex min-w-0", children: [_jsx("div", { ref: listRef, role: "tablist", "aria-label": label, className: cn('relative flex min-w-0 gap-1', overflow === 'scroll' ? 'overflow-x-auto' : 'overflow-hidden', pills && 'rounded-lg bg-surface-subtle p-1'), children: shown.map((t) => {
                                    const selected = t.id === value;
                                    return (_jsx("button", { ref: (el) => {
                                            refs.current[t.id] = el;
                                        }, id: `${base}-tab-${t.id}`, type: "button", role: "tab", "aria-selected": selected, "aria-controls": `${base}-panel-${t.id}`, tabIndex: selected ? 0 : -1, disabled: t.disabled, onClick: () => {
                                            onChange(t.id);
                                        }, onKeyDown: onKey, "data-testid": `tab-${t.id}`, className: cn('inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap text-base transition-colors duration-fast', 'disabled:cursor-not-allowed disabled:text-disabled-foreground', pills
                                            ? cn('h-9 rounded-md px-3 font-medium', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus', selected
                                                ? 'bg-surface text-primary shadow-card'
                                                : 'text-fg-muted hover:bg-surface/70 hover:text-fg')
                                            : cn('-mb-px h-11 border-b-2 px-4 font-semibold', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus', selected
                                                ? 'border-primary text-primary'
                                                : // Z6 T-8: fare üstünde alt çizgi YOK — alt çizgi yalnız seçili sekmenin işaretidir (fare üstü sekme
                                                    // ikinci bir "seçili" sekme gibi görünüyordu); fare üstü yalnız metni koyulaştırır
                                                    'border-transparent text-fg-muted hover:text-fg')), children: t.label }, t.id));
                                }) }), overflow === 'scroll' && edges.start ? (_jsx("button", { type: "button", tabIndex: -1, "aria-label": uiStrings.tabsScrollPrev, onClick: () => {
                                    scrollStrip(-1);
                                }, "data-testid": testId ? `${testId}-scroll-prev` : 'tabs-scroll-prev', className: cn('absolute inset-y-0 left-0 z-10 inline-flex w-11 items-center justify-center border-r border-border text-fg-muted shadow-card hover:text-fg', pills ? 'rounded-l-lg bg-surface-subtle' : 'bg-surface'), children: _jsx(ChevronLeft, { size: 18, "aria-hidden": "true" }) })) : null, overflow === 'scroll' && edges.end ? (_jsx("button", { type: "button", tabIndex: -1, "aria-label": uiStrings.tabsScrollNext, onClick: () => {
                                    scrollStrip(1);
                                }, "data-testid": testId ? `${testId}-scroll-next` : 'tabs-scroll-next', className: cn('absolute inset-y-0 right-0 z-10 inline-flex w-11 items-center justify-center border-l border-border text-fg-muted shadow-card hover:text-fg', pills ? 'rounded-r-lg bg-surface-subtle' : 'bg-surface'), children: _jsx(ChevronRight, { size: 18, "aria-hidden": "true" }) })) : null] }), hidden.length > 0 ? (_jsx("span", { ref: moreRef, className: "shrink-0 pl-1", children: _jsx(Menu, { label: moreLabel, align: "end", triggerTestId: testId ? `${testId}-more` : 'tabs-more', triggerClassName: cn('font-semibold text-fg-muted hover:text-fg', pills ? 'h-9 px-3' : '-mb-px h-11 border-b-2 border-transparent px-3'), trigger: _jsxs(_Fragment, { children: [_jsx("span", { className: "text-base", children: uiStrings.moreTabsShort }), _jsx(ChevronDown, { size: 16, "aria-hidden": "true" })] }), items: hidden.map((t, i) => ({
                                id: t.id,
                                label: t.menuLabel ?? t.label,
                                disabled: t.disabled === true,
                                testId: `tab-more-${t.id}`,
                                ...(t.menuGroup !== undefined && t.menuGroup !== hidden[i - 1]?.menuGroup
                                    ? { groupLabel: t.menuGroup }
                                    : {}),
                                onSelect: () => {
                                    onChange(t.id);
                                    pendingFocus.current = t.id;
                                },
                            })) }) })) : null] }), current ? (_jsx("div", { role: "tabpanel", id: `${base}-panel-${current.id}`, "aria-labelledby": `${base}-tab-${current.id}`, tabIndex: 0, className: cn('focus-visible:outline-none', panelClassName ?? 'pt-4'), children: current.panel })) : null] }));
}
// ---------------------------------------------------------------------------
// Tanım listesi (meta veri) ve metrik
// ---------------------------------------------------------------------------
export function DescriptionList({ items, layout = 'side', className, }) {
    return (_jsx("dl", { className: cn('text-base', layout === 'side'
            ? 'grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-[minmax(8rem,12rem)_1fr]'
            : 'space-y-3', className), children: items.map((it) => (_jsxs("div", { className: layout === 'side' ? 'contents' : undefined, children: [_jsx("dt", { className: "text-fg-subtle", children: it.term }), _jsx("dd", { className: "min-w-0 break-words text-fg", children: it.value })] }, it.key ?? it.term))) }));
}
export function Stat({ label, value, hint, className, }) {
    return (_jsxs("div", { className: cn('min-w-0', className), children: [_jsx("p", { className: "text-sm text-fg-subtle", children: label }), _jsx("p", { className: "text-lg font-semibold tabular-nums text-fg", children: value }), hint ? _jsx("p", { className: "text-sm text-fg-muted", children: hint }) : null] }));
}
//# sourceMappingURL=data-display.js.map